import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { AlertHeader, Button, Checkbox, FileUploadDropZone, H3, LoaderSpin, Message, ModalFactory, Text } from 'components';
import { theme, toRem } from 'design';
import { Appearance } from 'design/types';
import { selectAPIRequestErrors } from 'store/ApiClient';
import { importCalculatorSettingsRequest, TipCalculator } from 'store/TipCalculator';
import { IStore } from 'store/types';
import { IExportCalculatorSettings } from 'types';

const ModalText = styled(Text)`
  font-size: ${toRem(14)};
`;

const Semibold = styled.span`
  font-weight: ${theme.font.weight.medium};
`;

const ModalContent = styled.div`
  flex-direction: column;
  text-align: center;
  margin-top: ${toRem(24)};
  gap: ${toRem(24)};
  width: 100%;

  .message {
    padding: ${toRem(8)};
  }

  header {
    div {
      margin: 0;
    }
  }
`;

const OptionsContainer = styled.div`
  text-align: left;
  flex-direction: column;
  gap: ${toRem(12)};
  margin-bottom: ${toRem(24)};
`;

interface IImportSettingsModal {
  hideModal: () => void;
  siteId: string;
}

const MAX_FILE_SIZE = 20000000; // Max file size in bytes

const enum ImportSteps {
  UPLOAD = 'upload',
  CONFIRM = 'confirm',
  LOADING = 'loading',
  SUCCESS = 'success',
  ERROR = 'error',
}

const extensionError = { title: 'Invalid file extension', message: 'Please validate that the file extension is .json' };
const maxSizeError = { title: 'Max file size reached', message: 'The file you are uploading is greater than 20MB' };
const formatError = { title: 'Invalid file format', message: 'Please check the file and try again' };

export const ImportSettingsModal = ({ hideModal, siteId }: IImportSettingsModal) => {
  const dispatch = useDispatch();
  const apiError = useSelector((state: IStore) => selectAPIRequestErrors(state, TipCalculator.IMPORT_CALCULATOR_SETTINGS_REQUEST));
  const [step, setStep] = useState<ImportSteps>(ImportSteps.UPLOAD);
  const [fileContent, setFileContent] = useState<IExportCalculatorSettings>();
  const [importError, setImportError] = useState<{ title: string; message: string }>();
  const [importOptions, setImportOptions] = useState({ ruleSettings: false, segmentSettings: false });

  const onToggleCheckbox = (e: React.ChangeEvent<HTMLInputElement>, option: string) => {
    setImportOptions({ ...importOptions, [option]: e.target.checked });
  };

  const handleUpload = async (file: File) => {
    if (file.type !== 'application/json') {
      setImportError(extensionError);
    } else if (file.size > MAX_FILE_SIZE) {
      setImportError(maxSizeError);
    } else {
      const fileReader = new FileReader();

      fileReader.readAsText(file, 'UTF-8');
      fileReader.onload = (e) => {
        try {
          const calculatorSettings: IExportCalculatorSettings = JSON.parse(e.target.result as string);
          const importOptions = {
            ruleSettings: !!calculatorSettings.ruleSettings,
            segmentSettings: calculatorSettings.segmentSettings?.segments?.length > 0,
          };

          if (!importOptions.ruleSettings && !importOptions.segmentSettings) {
            // need at least one option available to import
            setImportError(formatError);
          } else {
            setImportOptions(importOptions);
            setFileContent(calculatorSettings);
            setStep(ImportSteps.CONFIRM);
          }
        } catch (error) {
          setImportError(formatError);
        }
      };
    }
  };

  const importSettings = () => {
    setStep(ImportSteps.LOADING);
    const { exportedAt, ruleSettings, segmentSettings, site } = fileContent;
    const payload = {
      exportedAt,
      ...(importOptions.ruleSettings && { ruleSettings }),
      ...(importOptions.segmentSettings && { segmentSettings }),
      site,
    };
    dispatch(
      importCalculatorSettingsRequest(
        siteId,
        payload,
        () => setStep(ImportSteps.SUCCESS),
        () => setStep(ImportSteps.ERROR)
      )
    );
  };

  return (
    <ModalFactory title="Import Calculator Settings" onClose={hideModal} isActive maxWidth={550} closeOnOutSideClick={false} hideCloseButton={step === ImportSteps.LOADING}>
      <ModalContent>
        {step === ImportSteps.UPLOAD && (
          <>
            {importError && <Message className="message" appearance={Appearance.WARNING} {...importError} />}
            <FileUploadDropZone
              instructions={
                <ModalText>
                  Drag your <Semibold>calculator settings file</Semibold>
                  <br />
                  here to get started
                </ModalText>
              }
              onUpload={handleUpload}
              accept=".json"
            />
          </>
        )}

        {step === ImportSteps.CONFIRM && (
          <>
            <OptionsContainer>
              <ModalText>Select the options you would like to import below</ModalText>
              <Checkbox id="tipCalculator" label="Tip Calculator Rules" checked={importOptions.ruleSettings} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onToggleCheckbox(e, 'ruleSettings')} disabled={!fileContent?.ruleSettings} />
              <Checkbox
                id="daySegments"
                label="Day Segments"
                checked={importOptions.segmentSettings}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onToggleCheckbox(e, 'segmentSettings')}
                disabled={!fileContent?.segmentSettings?.segments?.length}
              />
            </OptionsContainer>

            <Message className="message" appearance={Appearance.WARNING} title="These changes are final" message="It's recommended you export current settings to keep as backup before attempting an import." />
            <Button label="Import Tip Calculator Settings" data-cy="confirm-import-button" onClick={importSettings} disabled={!importOptions.ruleSettings && !importOptions.segmentSettings} />
          </>
        )}

        {step === ImportSteps.LOADING && (
          <>
            <LoaderSpin size={60} stroke={6} color={theme.color.secondary[400]} />
            <ModalText>Importing Calculator Settings...</ModalText>
          </>
        )}

        {step === ImportSteps.SUCCESS && (
          <>
            <AlertHeader icon="Success" iconFill={theme.color.secondary[400]} size={64} />
            <ModalText data-cy="import-success-message">Calculator Settings Imported Successfully!</ModalText>
          </>
        )}

        {step === ImportSteps.ERROR && (
          <>
            <AlertHeader size={64} />
            <H3>We are having trouble importing</H3>
            <ModalText>
              We encountered an issue importing the calculator
              <br />
              settings for this site.
            </ModalText>
            <Message className="message" appearance={Appearance.WARNING} message={apiError?.errorMessages[0].replace(/rule_settings\.type (standard|advanced)/, '')} />
          </>
        )}
      </ModalContent>
    </ModalFactory>
  );
};
